import React, { useEffect, useState } from 'react';

import type { TagEntity, TopTagResponse } from '@schibsted/frapi-schema';
import type { FrapiTeaserItem } from '../../../types/frapi-types.js';

import { useByfunnPulseContext } from '../../../pulse/index.js';
import { useSearch } from '../../hooks/useSearch.js';
import { sectionMappings } from '../../layouts/front/sectionHelpers.js';
import { FilterButton } from './FilterButton.js';
import FilterLinkButton from './FilterLinkButton.js';
import { FilterToggle } from './FilterToggle.js';
import { filterMappings, removeDuplicates } from './helpers.js';
import { ResetFilterButton } from './ResetFilterButton.js';
import { SearchBarInput } from './SearchBarInput.js';
import { SearchBarPreviewResults } from './SearchBarPreviewResults.js';
import { SearchFilters } from './SearchFilters.js';

export interface SearchBarProps {
  activeSection: string;
  isMobile: boolean;
  presetTerm?: string;
  presetActiveFilters?: TagEntity[];
  filterString?: string;
  setActiveSection: (text: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  activeSection,
  isMobile,
  presetTerm,
  presetActiveFilters,
  filterString,
}) => {
  const [section, setSection] = useState(activeSection);
  const [activeFilters, setActiveFilters] = useState<TagEntity[]>([]);

  const { trackClickUIElement } = useByfunnPulseContext();

  const trackSearch = (searchTerm: string): void => {
    // TODO: searchTerm?
    trackClickUIElement(
      {
        object: {
          id: 'search-bar',
          name: 'perform search',
          elementType: 'Input',
          custom: {
            query: searchTerm,
          },
        },
      },
      {
        object: {
          objectId: 'search-bar',
          name: 'perform search',
          elementType: 'Input',
        },
        placement: { containerName: 'page' },
      },
    );
  };

  const { searchTerm, searchResult, setSearchTerm, debouncedSearchTerm } =
    useSearch({ section, activeFilters, presetTerm, callback: trackSearch });

  useEffect(() => {
    setSection(activeSection);
    setOpenFilter(false);
    setActiveFilters([]);
    setSearchTerm('');
  }, [activeSection, setSearchTerm]);

  const [filters, setFilters] = useState<TopTagResponse>();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (presetActiveFilters) {
      setActiveFilters(presetActiveFilters);
    }
  }, [presetActiveFilters]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isOpenFilter]);

  const setFilter = (filter: TagEntity) => {
    const filterExists = activeFilters.some((f) => f.id === filter.id);
    if (!filterExists) {
      setActiveFilters([...activeFilters, filter]);
    } else {
      setActiveFilters(activeFilters.filter((f) => f.id !== filter.id));
    }
  };

  const articles = searchResult?.articles;
  const hasResults = articles && searchTerm;
  const hasActiveFilters = activeFilters.length > 0;
  const activeFiltersCollapsed = hasActiveFilters && !isOpenFilter;

  let articlesNoDuplicates: FrapiTeaserItem[] = [];
  const shouldRemoveDuplicates =
    articles &&
    (activeSection === 'mat-og-drikke' || activeSection === 'film-og-serier');
  if (shouldRemoveDuplicates) {
    articlesNoDuplicates = removeDuplicates(articles);
  }

  return (
    <div
      className={`search-container ${
        isOpenFilter ? 'toggle-on-filters' : 'toggle-off-filters'
      }`}
    >
      <div className="search-bar">
        {presetTerm && (
          <h1 className="serp-title">{`${
            sectionMappings[section]?.searchTitle || 'Vink'
          }`}</h1>
        )}

        <SearchBarInput
          searchResult={searchResult}
          searchTerm={searchTerm}
          section={section}
          setSearchTerm={setSearchTerm}
          setShowPreview={setShowPreview}
          presetTerm={presetTerm}
          activeFilters={activeFilters}
        />
      </div>
      {hasResults && showPreview && searchTerm !== presetTerm && (
        <SearchBarPreviewResults
          articles={shouldRemoveDuplicates ? articlesNoDuplicates : articles}
          searchTerm={searchTerm}
          debouncedSearchTerm={debouncedSearchTerm}
          searchResult={searchResult}
          isMobile={isMobile}
          section={section}
          activeFilters={activeFilters}
        />
      )}
      {activeFiltersCollapsed && (
        <div className="filter-wrapper">
          {activeFilters.map((tag, i) => {
            return filterString ? (
              <FilterLinkButton
                key={`active-filter-${i}`}
                section={section}
                tag={tag}
                filterTags={filterString}
                presetTerm={presetTerm}
              />
            ) : (
              <FilterButton
                key={`active-filter-${i}`}
                tag={tag}
                handleClick={setFilter}
                active
              />
            );
          })}
        </div>
      )}
      {filterMappings[section] && (
        <div className="button-wrapper">
          {(!filters || !isOpenFilter) && (
            <FilterToggle
              sectionId={sectionMappings[section]?.sectionId}
              hasActiveFilters={hasActiveFilters}
              setFilters={setFilters}
              setOpenFilter={setOpenFilter}
            />
          )}
          {activeFiltersCollapsed && (
            <ResetFilterButton
              searchTerm={searchTerm}
              presetTerm={presetTerm}
              section={section}
            />
          )}
        </div>
      )}
      {filters && isOpenFilter && (
        <SearchFilters
          {...filters}
          activeFilters={activeFilters}
          setFilter={setFilter}
          setOpen={setOpenFilter}
          section={section}
          setActiveFilters={setActiveFilters}
          searchTerm={searchTerm}
          presetTerm={presetTerm}
        />
      )}
    </div>
  );
};
